import { Autocomplete, SxProps, TextField } from "@mui/material";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";

type TOption = {
  value: string;
  label: string;
};

type TRHFAutocomplete = {
  name: string;
  options: readonly TOption[];
  label?: string;
  onChange?: (val: string) => void;
  disabled?: boolean;
  sx?: SxProps;
  noOptionsText?: string;
  disablePortal?: boolean;
  id?: string;
  size?: "small" | "medium";
  defaultValue?: string;
  rules?: RegisterOptions;
};

export function RHFAutocomplete({
  name,
  options,
  label,
  onChange,
  disabled,
  sx,
  noOptionsText,
  disablePortal,
  id,
  size,
  defaultValue,
  rules,
  ...rest
}: TRHFAutocomplete) {
  const { control, setValue } = useFormContext();

  function getOptionLabel(val: string | TOption) {
    if (typeof val === "object" && val !== null) {
      const labelSelected = options.find((opt) => opt.value === val.value);
      return labelSelected?.label || "";
    } else {
      const labelSelected = options.find((opt) => opt.value === val);
      return labelSelected?.label || "";
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        return (
          <Autocomplete
            {...field}
            disabled={disabled}
            value={field.value || null}
            getOptionLabel={(val) => getOptionLabel(val)}
            onChange={(_, newValue) => {
              if (onChange) {
                onChange(newValue?.value);
              }
              setValue(name, newValue?.value || null, { shouldValidate: true });
            }}
            sx={sx}
            isOptionEqualToValue={(option, value) => {
              if (typeof value === "object" && value !== null) {
                return option.value === value.value;
              }

              return option.value === value;
            }}
            disablePortal={disablePortal}
            options={options}
            id={id}
            size={size}
            noOptionsText={noOptionsText}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant="outlined"
                error={!!error}
                helperText={error?.message || ""}
              />
            )}
            {...rest}
          />
        );
      }}
    />
  );
}
